// Shared gesture bottom sheet for the Seeker & Hider shells: 3 detents
// (peek/half/full) with grabber drag + an explicit ▾/▴ collapse toggle.
// Expects the markup ids #sk-sheet, #sk-grab, #sk-sheet-toggle to exist.

type DetentName = "peek" | "half" | "full";

export interface Sheet {
  /** Currently-selected detent (independent of collapsed state). */
  readonly name: DetentName;
  /** Snap the sheet to a detent (no-op on height while collapsed). */
  set(name: DetentName): void;
  /** Collapse the sheet to its slim bar (full-screen map) or restore it. */
  collapse(c: boolean): void;
}

export function mountSheet(root: HTMLElement, onResize: () => void): Sheet {
  const sheet = root.querySelector<HTMLElement>("#sk-sheet")!;
  const H = () => window.innerHeight;
  const detents = () => ({ peek: 168, half: Math.min(452, Math.round(H() * 0.56)), full: Math.min(760, Math.round(H() * 0.9)) });
  const SHEET_COLLAPSED = 42; // slim bar with just the grab handle + toggle
  let sheetName: DetentName = "half";
  let collapsed = false;
  let full = false; // edge-to-edge over the map (optional ⛶ button; hider only)
  const applySheet = (px: number) => { sheet.style.height = px + "px"; };
  const applyHeight = () => { if (full) return; applySheet(collapsed ? SHEET_COLLAPSED : detents()[sheetName]); }; // .sk-fullscreen drives height in CSS
  const setSheet = (name: DetentName) => { sheetName = name; if (!collapsed && !full) applySheet(detents()[name]); };
  // Explicit show/hide button — drag-to-collapse isn't reliable on touch.
  const toggleBtn = root.querySelector<HTMLButtonElement>("#sk-sheet-toggle")!;
  // Optional full-screen button (present only in the hider shell's markup).
  const fullBtn = root.querySelector<HTMLButtonElement>("#sk-sheet-full");
  const syncBtns = () => {
    toggleBtn.textContent = collapsed ? "▴" : "▾";
    toggleBtn.setAttribute("aria-label", collapsed ? "Show panel" : "Hide panel");
    toggleBtn.title = collapsed ? "Show panel" : "Hide panel";
    if (fullBtn) {
      fullBtn.textContent = full ? "⤡" : "⛶";
      fullBtn.setAttribute("aria-label", full ? "Exit full screen" : "Full screen");
      fullBtn.title = full ? "Exit full screen" : "Full screen";
    }
  };
  function setCollapsed(c: boolean) {
    collapsed = c;
    if (c && full) { full = false; sheet.classList.remove("sk-fullscreen"); } // collapsing exits full screen
    sheet.classList.toggle("sk-collapsed", c);
    syncBtns(); applyHeight(); onResize();
  }
  function setFull(f: boolean) {
    full = f;
    if (f && collapsed) { collapsed = false; sheet.classList.remove("sk-collapsed"); } // going full screen un-collapses
    sheet.classList.toggle("sk-fullscreen", f);
    syncBtns(); applyHeight(); onResize();
  }
  toggleBtn.addEventListener("pointerdown", (e) => e.stopPropagation());
  toggleBtn.addEventListener("click", (e) => { e.stopPropagation(); setCollapsed(!collapsed); });
  fullBtn?.addEventListener("pointerdown", (e) => e.stopPropagation());
  fullBtn?.addEventListener("click", (e) => { e.stopPropagation(); setFull(!full); });

  const grab = root.querySelector<HTMLElement>("#sk-grab")!;
  let startY = 0, startPx = 0, dragging = false, moved = false;
  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    if (Math.abs(startY - e.clientY) > 4) moved = true;
    const d = detents();
    applySheet(Math.max(d.peek - 30, Math.min(d.full + 30, startPx + (startY - e.clientY))));
  };
  const onUp = () => {
    if (!dragging) return; dragging = false;
    const d = detents(); const px = sheet.getBoundingClientRect().height;
    const name = (["peek", "half", "full"] as const).reduce((a, b) => Math.abs(d[b] - px) < Math.abs(d[a] - px) ? b : a);
    setSheet(name);
    window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp);
  };
  grab.addEventListener("pointerdown", (e) => {
    if (collapsed || full) return; // collapsed/full only toggle via tap, no drag
    dragging = true; moved = false; startY = e.clientY; startPx = sheet.getBoundingClientRect().height;
    window.addEventListener("pointermove", onMove); window.addEventListener("pointerup", onUp);
  });
  grab.addEventListener("click", () => {
    if (full) { setFull(false); return; } // tap the handle to drop out of full screen
    if (collapsed) { setCollapsed(false); return; }
    if (moved) return;
    const order = ["peek", "half", "full"] as const; setSheet(order[(order.indexOf(sheetName) + 1) % 3]);
  });

  setSheet("half");

  return {
    get name() { return sheetName; },
    set: setSheet,
    collapse: (c: boolean) => { if (collapsed !== c) setCollapsed(c); },
  };
}
